/* eslint-disable complexity */
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan';
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { KalenderArbeitsbereich } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/KalenderArbeitsbereich';
import { KalenderMitarbeiter } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/KalenderMitarbeiter';
import { KalenderStationByArbeitsbereich } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/KalenderStation';

type Event = {
  date: Date;
  type: 'start' | 'end';
  stationId: string;
  abschnittId: string;
  mitarbeiter: KalenderMitarbeiter;
  lernziele: string;
};

/* eslint-disable max-lines-per-function */
export function transformMitarbeiterStationenToArbeitsbereichStationen(
  data: Rotationsplan[],
): KalenderArbeitsbereich[] {
  const mitarbeiter: KalenderMitarbeiter[] = data.map((plan) => {
    return new KalenderMitarbeiter({
      id: plan.mitarbeiter.id,
      beruf: plan.beruf,
      lehrjahr: plan.getCurrentLehrjahr(),
      vorname: plan.mitarbeiter.vorname,
      nachname: plan.mitarbeiter.nachname,
      imgUrl: plan.mitarbeiter.imgUrl,
      stationen: plan
        .getAllStationen()
        .filter((station) => station instanceof ArbeitsbereichStation),
    });
  });

  const arbeitsbereicheMap: Record<
    string,
    {
      arbeitsbereich: KalenderArbeitsbereich;
      stationen: Array<{
        id: string;
        von: Date;
        bis: Date;
        mitarbeiter: KalenderMitarbeiter;
        abschnittId: string;
        lernziele: string;
      }>;
    }
  > = {};

  mitarbeiter.forEach((m) => {
    m.stationen?.forEach((station) => {
      const bereichId = station.arbeitsbereich.id;
      if (!arbeitsbereicheMap[bereichId]) {
        arbeitsbereicheMap[bereichId] = {
          arbeitsbereich: new KalenderArbeitsbereich({
            id: bereichId,
            name: station.arbeitsbereich.name,
            markierung: station.arbeitsbereich.markierung,
            standort: station.arbeitsbereich.standort,
          }),
          stationen: [],
        };
      }
      arbeitsbereicheMap[bereichId].stationen.push({
        id: station.id,
        von: station.von,
        bis: station.bis,
        abschnittId: station.abschnittId,
        mitarbeiter: new KalenderMitarbeiter({
          id: m.id,
          vorname: m.vorname,
          nachname: m.nachname,
          beruf: m.beruf,
          lehrjahr: m.lehrjahr,
        }),
        lernziele: station.lernziele,
      });
    });
  });

  const result: KalenderArbeitsbereich[] = [];
  Object.values(arbeitsbereicheMap).forEach(({ arbeitsbereich, stationen }) => {
    const events: Event[] = [];

    stationen.forEach(
      ({ id, von, bis, mitarbeiter, abschnittId, lernziele }) => {
        events.push({
          stationId: id,
          date: von,
          type: 'start',
          mitarbeiter,
          abschnittId,
          lernziele,
        });
        const bisPlus1 = new Date(bis);
        bisPlus1.setDate(bisPlus1.getDate() + 1);
        events.push({
          stationId: id,
          date: bisPlus1,
          type: 'end',
          mitarbeiter,
          abschnittId,
          lernziele,
        });
      },
    );

    events.sort((a, b) => {
      if (a.date.getTime() !== b.date.getTime())
        return a.date.getTime() - b.date.getTime();
      return a.type === 'start' ? -1 : 1;
    });

    let active: KalenderMitarbeiter[] = [];
    let lastDate: Date | null = null;
    const arbeitsbereicheStationen: KalenderStationByArbeitsbereich[] = [];

    for (const event of events) {
      if (lastDate && lastDate < event.date) {
        const bis = new Date(event.date);
        bis.setDate(bis.getDate() - 1);
        if (active.length > 0 && lastDate <= bis) {
          arbeitsbereicheStationen.push(
            new KalenderStationByArbeitsbereich({
              station: new ArbeitsbereichStation({
                id: event.stationId,
                von: lastDate,
                bis,
                abschnittId: event.abschnittId,
                arbeitsbereich,
                lernziele: event.lernziele,
              }),
              mitarbeiter: active.map((m) => m),
            }),
          );
        }
      }

      if (event.type === 'start') {
        active.push(event.mitarbeiter);
      } else {
        active = active.filter((m) => m.id !== event.mitarbeiter.id);
      }
      lastDate = event.date;
    }
    arbeitsbereich.stationen = arbeitsbereicheStationen;
    result.push(arbeitsbereich);
  });
  return result;
}
